import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Label, HelperText, ErrorText } from './form-helpers.js';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, helperText, errorText, className, id: idProp, disabled, children, ...rest },
  ref,
) {
  const generated = useId();
  const id = idProp ?? generated;
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div
        className={cn(
          'relative flex items-stretch rounded-md bg-bg-surface-2 border border-border-strong transition-colors',
          'focus-within:border-accent-base focus-within:ring-2 focus-within:ring-accent-base/30',
          errorText && 'border-danger focus-within:border-danger focus-within:ring-danger/30',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        <select
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={!!errorText || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-12 w-full appearance-none bg-transparent pl-3.5 pr-10 text-base text-text-primary outline-none',
            className,
          )}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-4 text-text-secondary"
        />
      </div>
      {errorText && <ErrorText id={errorId}>{errorText}</ErrorText>}
      {helperText && !errorText && <HelperText>{helperText}</HelperText>}
    </div>
  );
});
